const vertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const fragmentShader = `
uniform sampler2D uTexture;
varying vec2 vUv;
uniform vec2 uResolution;
uniform float uProgress;
uniform vec3 uColor;
uniform vec2 uContainerRes;
uniform float uOpacity;
uniform float uGrayscale;

float random (vec2 st) {
    return fract(sin(dot(st.xy, vec2(12.9898,78.233)))* 43758.5453123);
}

void main() {
    float imageAspect = uResolution.x / uResolution.y;
    float containerAspect = uContainerRes.x / uContainerRes.y;
    
    vec2 ratio = vec2(
        min(containerAspect / imageAspect, 1.0),
        min(imageAspect / containerAspect, 1.0)
    );
    
    vec2 coverUvs = vec2(
        vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
        vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
    );

    // Grid Logic
    float gridSize = floor(uContainerRes.x / 40.0);
    gridSize = max(gridSize, 1.0);
    
    vec2 gridMultiplier = vec2(gridSize, gridSize / containerAspect);
    
    vec2 gridUv = vec2(
        floor(coverUvs.x * gridMultiplier.x) / gridMultiplier.x,
        floor(coverUvs.y * gridMultiplier.y) / gridMultiplier.y
    );
    
    float r = random(gridUv); // Random value for each grid cell
    
    // Threshold calculation for staggered "rain" reveal
    // Top-to-bottom: gridUv.y=1 is top, gridUv.y=0 is bottom
    float delay = (1.0 - gridUv.y) * 0.5 + r * 0.6;
    
    // Sharp transition: either dark or image
    float reveal = smoothstep(delay, delay + 0.2, uProgress * 1.8);
    
    vec4 dark = vec4(uColor, 1.0);
    vec4 texColor = texture2D(uTexture, coverUvs);
    
    // Optional grayscale
    float luma = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
    vec3 gray = vec3(luma);
    texColor.rgb = mix(texColor.rgb, gray, uGrayscale);

    // Pixels are dark until reveal
    vec4 finalColor = mix(dark, texColor, reveal);
    
    // For dark pixels (not yet revealed) alpha is 1 so they're solid.
    // Once revealed, respect uOpacity for the final image.
    float alpha;
    if (reveal > 0.5) {
        alpha = texColor.a * uOpacity;
    } else {
        // Dark pixel: only show if uOpacity is full (don't show dark blobs over bg for transparent images)
        alpha = (1.0 - uOpacity) < 0.01 ? 1.0 : reveal * 2.0 * uOpacity;
    }

    gl_FragColor = vec4(finalColor.rgb, alpha);
}
`;

class WebGL {
    static instance = null;

    constructor() {
        if (WebGL.instance) return WebGL.instance;
        WebGL.instance = this;

        this.container = document.body;
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this.scene = new THREE.Scene();

        const perspective = 1000;
        const fov = (180 * (2 * Math.atan(this.height / 2 / perspective))) / Math.PI;
        this.camera = new THREE.PerspectiveCamera(
            fov,
            this.width / this.height,
            1,
            perspective * 2,
        );
        this.camera.position.set(0, 0, perspective);

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setSize(this.width, this.height);
        this.renderer.domElement.style.position = 'fixed';
        this.renderer.domElement.style.top = 0;
        this.renderer.domElement.style.left = 0;
        this.renderer.domElement.style.pointerEvents = 'none';
        this.renderer.domElement.style.zIndex = '500';
        this.container.appendChild(this.renderer.domElement);

        this.meshes = [];
        this.init();
    }

    init() {
    // Target Home elements, Project Hero images, and Next Project bg images
        const imageElements = document.querySelectorAll(
            '.intro__gallery-item, .intro__gallery-img, .present__visual, .footer__img, .content__intro-img, .divider-image, .about-image-mid img, .project-list__img',
        );
        const nextProjectBgImages = document.querySelectorAll(
            '.next-project__bg img',
        );

        this.meshes = [];

        if (typeof imagesLoaded !== 'undefined') {
            imagesLoaded(Array.from(imageElements), () => {
                imageElements.forEach((el) => {
                    const media = new Media(el, this.scene);
                    if (media.mesh) {
                        this.meshes.push(media);
                    }
                });
                if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
            });

            // Next project bg images: pixel-rain reveal, then sit at low opacity as atmospheric bg
            if (nextProjectBgImages.length > 0) {
                imagesLoaded(Array.from(nextProjectBgImages), () => {
                    nextProjectBgImages.forEach((el) => {
                        const media = new Media(el, this.scene, {
                            scrollStart: 'top 95%',
                            duration: 2.5,
                            opacity: 0.15,
                            grayscale: 1.0,
                            color: '#F3F4EF', // sand bg — reveals from page bg into image, no dark blobs
                        });
                        if (media.mesh) {
                            this.meshes.push(media);
                        }
                    });
                    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
                });
            }
        }

        this.animate();
        this.handleResize();
    }

    handleResize() {
        window.addEventListener('resize', () => {
            this.width = window.innerWidth;
            this.height = window.innerHeight;
            this.renderer.setSize(this.width, this.height);
            this.camera.aspect = this.width / this.height;
            const perspective = 1000;
            this.camera.fov = (180 * (2 * Math.atan(this.height / 2 / perspective))) / Math.PI;
            this.camera.updateProjectionMatrix();
            if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
        });
    }

    animate() {
        this.meshes.forEach((mesh) => mesh.update());
        this.renderer.render(this.scene, this.camera);
        requestAnimationFrame(this.animate.bind(this));
    }
}

class Media {
    constructor(el, scene, options = {}) {
        this.el = el;
        this.scene = scene;
        this.options = options;
        this.bounds = this.el.getBoundingClientRect();

        let src = '';
        if (this.el.tagName === 'IMG') {
            src = this.el.getAttribute('src');
        } else {
            const style = window.getComputedStyle(this.el);
            const bg = style.backgroundImage;
            if (bg && bg !== 'none') {
                src = bg.replace(/url\(['"]?(.*?)['"]?\)/, '$1');
            }
        }

        if (!src) return;

        this.geometry = new THREE.PlaneBufferGeometry(1, 1);
        this.texture = new THREE.TextureLoader().load(src, (tex) => {
            if (this.material) {
                this.material.uniforms.uResolution.value.x = tex.image.width;
                this.material.uniforms.uResolution.value.y = tex.image.height;
            }
        });

        this.material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            uniforms: {
                uTexture: { value: this.texture },
                uProgress: { value: 0 },
                uColor: { value: new THREE.Color(options.color || '#23272C') },
                uResolution: { value: new THREE.Vector2(1, 1) },
                uContainerRes: {
                    value: new THREE.Vector2(
                        Math.max(this.bounds.width, 1),
                        Math.max(this.bounds.height, 1),
                    ),
                },
                uOpacity: {
                    value: options.opacity !== undefined ? options.opacity : 1.0,
                },
                uGrayscale: {
                    value: options.grayscale !== undefined ? options.grayscale : 0.0,
                },
            },
        });

        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.mesh.scale.set(this.bounds.width, this.bounds.height, 1);
        this.scene.add(this.mesh);

        this.el.style.opacity = '0';
        this.initAnimation();
    }

    initAnimation() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

        const scrollStart = this.options.scrollStart || 'top 85%';
        const duration = this.options.duration || 2.0;

        gsap.to(this.material.uniforms.uProgress, {
            value: 1,
            duration,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: this.el,
                start: scrollStart,
                toggleActions: 'play none none reverse',
            },
        });
    }

    update() {
        if (!this.mesh) return;
        this.bounds = this.el.getBoundingClientRect();
        this.mesh.scale.set(this.bounds.width, this.bounds.height, 1);

        this.mesh.position.x = this.bounds.left - window.innerWidth / 2 + this.bounds.width / 2;
        this.mesh.position.y = -this.bounds.top + window.innerHeight / 2 - this.bounds.height / 2;

        this.material.uniforms.uContainerRes.value.x = Math.max(
            this.bounds.width,
            1,
        );
        this.material.uniforms.uContainerRes.value.y = Math.max(
            this.bounds.height,
            1,
        );
    }
}

window.addEventListener('load', () => {
    if (
        typeof gsap !== 'undefined'
    && typeof THREE !== 'undefined'
    && typeof ScrollTrigger !== 'undefined'
    && typeof imagesLoaded !== 'undefined'
    ) {
        new WebGL();
    }
});
